import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { Slot } from "radix-ui";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

/*
 * Buttons (Refined, 2026-11-04; was the 2026-10-16 rework). Solid fills, a 6px shape and one quiet
 * shadow: copper `accent` is the one main action on a screen, deep-teal `primary` a solid secondary
 * action, white `secondary` with a hairline for everything else. Hover darkens a step, press sinks
 * 1px, focus draws a jade ring, and a disabled main action stays readable (ivory, muted text).
 */
const DISABLED_SOLID =
  "disabled:border disabled:border-border disabled:bg-neutral-soft disabled:bg-none disabled:text-ink-muted disabled:opacity-100 disabled:shadow-none";
const button = cva(
  "relative inline-flex cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold tracking-[-0.005em] transition-[background-color,border-color,box-shadow,color] duration-150 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus/30 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: `border border-primary bg-primary text-primary-ink shadow-[0_1px_2px_rgb(20_32_30/0.12)] hover:border-primary-hover hover:bg-primary-hover active:shadow-none ${DISABLED_SOLID}`,
        // The one copper main action on a screen (Preview, Gate pass in, Invite user…).
        accent: `border border-accent-hover bg-accent text-accent-ink shadow-[0_1px_2px_rgb(20_32_30/0.12)] hover:bg-accent-hover active:shadow-none ${DISABLED_SOLID}`,
        secondary:
          "border border-border-strong bg-surface-raised text-ink shadow-[0_1px_1px_rgb(20_32_30/0.04)] hover:border-ink-muted/40 hover:bg-surface active:bg-surface-sunken active:shadow-none",
        outline:
          "border border-brand/50 bg-transparent text-primary-text hover:border-brand hover:bg-primary-soft active:bg-primary-soft/70",
        ghost:
          "text-ink-secondary hover:bg-surface-sunken hover:text-ink active:bg-border/60",
        danger:
          "border border-danger bg-danger text-danger-ink shadow-[0_1px_2px_rgb(20_32_30/0.12)] hover:brightness-95 active:shadow-none",
        link: "h-auto px-0 text-primary-text underline decoration-transparent underline-offset-4 hover:decoration-current active:translate-y-0",
      },
      size: {
        sm: "h-(--control-h-sm) px-3 text-xs",
        md: "h-(--control-h) px-4",
        lg: "h-(--control-h-lg) px-6 text-base",
        xl: "h-14 px-8 text-base",
        icon: "h-(--control-h) w-(--control-h)",
        "icon-sm": "h-(--control-h-sm) w-(--control-h-sm)",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof button> & {
    /** Render the child element (e.g. a router Link) with the button's classes. */
    asChild?: boolean | undefined;
    loading?: boolean | undefined;
    children?: ReactNode;
  };

export function Button({
  className,
  variant,
  size,
  type = "button",
  asChild,
  loading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  if (asChild) {
    // A slotted child (usually a router Link) is not a <button>, so `disabled` means nothing to it:
    // the pointer has to be taken away with classes, and the state said out loud with aria.
    const off = disabled || loading;
    return (
      <Slot.Root
        data-variant={variant ?? "primary"}
        className={cn(
          button({ variant, size }),
          off && "pointer-events-none opacity-50",
          className
        )}
        aria-disabled={off || undefined}
        aria-busy={loading || undefined}
        {...props}
      >
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      type={type}
      data-variant={variant ?? "primary"}
      className={cn(button({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}
